import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { useEffect, useState } from 'react';
import { cmp } from '@guardian/consent-management-platform';
import { getCookie } from '@guardian/libs';
import { getArticleCounts } from '../../../lib/article-count';
import { canShowRRBanner, canShowPuzzlesBanner, ReaderRevenueBanner, PuzzlesBanner, } from './ReaderRevenueBanner';
import { getAlreadyVisitedCount } from '../../lib/alreadyVisited';
import { useOnce } from '../../lib/useOnce';
import { pickMessage, } from '../../lib/messagePicker';
import { getLocaleCode } from '../../lib/getCountryCode';
import { useSignInGateWillShow } from '../../lib/useSignInGateWillShow';
import { BrazeBanner, canShowBrazeBanner } from './BrazeBanner';
const getBannerLastClosedAt = (key) => {
    const item = localStorage.getItem(`gu.prefs.${key}`);
    if (item) {
        const parsedItem = JSON.parse(item);
        return parsedItem.value;
    }
    return item;
};
const DEFAULT_BANNER_TIMEOUT_MILLIS = 2000;
const buildCmpBannerConfig = () => ({
    candidate: {
        id: 'cmpUi',
        canShow: () => cmp
            .willShowPrivacyMessage()
            .then((result) => result ? { show: true, meta: undefined } : { show: false }),
        show: () => {
            // New CMP is not a react component and is shown outside of react's world
            // so render nothing if it will show
            return null;
        },
    },
    timeoutMillis: null,
});
const buildRRBannerConfigWith = ({ id, BannerComponent, canShowFn, isEnabled, }) => {
    return ({ isSignedIn, asyncCountryCode, isPreview, asyncArticleCounts, signInGateWillShow = false, contentType, section, shouldHideReaderRevenue, isMinuteArticle, isPaidContent, isSensitive, tags, contributionsServiceUrl, idApiUrl, switches, }) => {
        return {
            candidate: {
                id,
                canShow: () => canShowFn({
                    remoteBannerConfig: isEnabled(switches),
                    isSignedIn,
                    asyncCountryCode,
                    contentType,
                    sectionId: section,
                    shouldHideReaderRevenue,
                    isMinuteArticle,
                    isPaidContent,
                    isSensitive,
                    tags,
                    contributionsServiceUrl,
                    alreadyVisitedCount: getAlreadyVisitedCount(),
                    engagementBannerLastClosedAt: getBannerLastClosedAt('engagementBannerLastClosedAt'),
                    subscriptionBannerLastClosedAt: getBannerLastClosedAt('subscriptionBannerLastClosedAt'),
                    section,
                    isPreview,
                    idApiUrl,
                    signInGateWillShow,
                    asyncArticleCounts,
                }),
                show: ({ meta, module, fetchEmail }) => () => (_jsx(BannerComponent, { meta: meta, module: module, fetchEmail: fetchEmail }, void 0)),
            },
            timeoutMillis: DEFAULT_BANNER_TIMEOUT_MILLIS,
        };
    };
};
const buildPuzzlesBannerConfig = buildRRBannerConfigWith({
    id: 'puzzles-banner',
    BannerComponent: PuzzlesBanner,
    canShowFn: canShowPuzzlesBanner,
    isEnabled: (switches) => switches.puzzlesBanner,
});
const buildReaderRevenueBannerConfig = buildRRBannerConfigWith({
    id: 'reader-revenue-banner',
    BannerComponent: ReaderRevenueBanner,
    canShowFn: canShowRRBanner,
    isEnabled: (switches) => switches.remoteBanner,
});
const buildBrazeBanner = (brazeMessages, brazeArticleContext) => ({
    candidate: {
        id: 'braze-banner',
        canShow: () => canShowBrazeBanner(brazeMessages, brazeArticleContext),
        show: (meta) => () => _jsx(BrazeBanner, { meta: meta }, void 0),
    },
    timeoutMillis: DEFAULT_BANNER_TIMEOUT_MILLIS,
});
export const StickyBottomBanner = ({ brazeMessages, contentType, sectionName, section, tags, isPaidContent, isPreview, shouldHideReaderRevenue, isMinuteArticle, isSensitive, contributionsServiceUrl, idApiUrl, switches, pageId, keywordsId, }) => {
    const asyncCountryCode = getLocaleCode();
    const isSignedIn = !!getCookie({ name: 'GU_U', shouldMemoize: true });
    const [SelectedBanner, setSelectedBanner] = useState(null);
    const [asyncArticleCounts, setAsyncArticleCounts] = useState();
    const signInGateWillShow = useSignInGateWillShow({
        isSignedIn,
        contentType,
        sectionName,
        tags,
        isPaidContent,
        isPreview,
    });
    useEffect(() => {
        setAsyncArticleCounts(getArticleCounts(pageId, keywordsId));
    }, [pageId, keywordsId]);
    useOnce(() => {
        const CMP = buildCmpBannerConfig();
        const puzzlesBanner = buildPuzzlesBannerConfig({
            isSignedIn,
            asyncCountryCode: asyncCountryCode,
            isPreview,
            asyncArticleCounts: asyncArticleCounts,
            contentType,
            section,
            shouldHideReaderRevenue,
            isMinuteArticle,
            isPaidContent,
            isSensitive,
            tags,
            contributionsServiceUrl,
            idApiUrl,
            switches,
        });
        const readerRevenue = buildReaderRevenueBannerConfig({
            isSignedIn,
            asyncCountryCode: asyncCountryCode,
            isPreview,
            asyncArticleCounts: asyncArticleCounts,
            signInGateWillShow,
            contentType,
            section,
            shouldHideReaderRevenue,
            isMinuteArticle,
            isPaidContent,
            isSensitive,
            tags,
            contributionsServiceUrl,
            idApiUrl,
            switches,
        });
        const brazeArticleContext = {
            section: sectionName,
        };
        const brazeBanner = buildBrazeBanner(brazeMessages, brazeArticleContext);
        const bannerConfig = {
            candidates: [CMP, puzzlesBanner, readerRevenue, brazeBanner],
            name: 'banner',
        };
        pickMessage(bannerConfig)
            .then((PickedBanner) => setSelectedBanner(PickedBanner))
            .catch((e) => console.error(`StickyBottomBanner pickMessage - error: ${e}`));
    }, [isSignedIn, asyncCountryCode, brazeMessages, asyncArticleCounts]);
    if (SelectedBanner) {
        return _jsx(SelectedBanner, {}, void 0);
    }
    return null;
};
